import _ from 'lodash';
import { Component, OnInit, EventEmitter, Output, Input, inject, DestroyRef } from '@angular/core';
import { FormArray, FormBuilder, FormControl } from '@angular/forms';
import { filter } from 'rxjs/operators';

import * as ExcelJS from 'exceljs/dist/exceljs';
import { CampaignService } from 'app/campaign/services/campaign.service';
import { FUNDRAISER_COLUMNS } from '../../enums/champions-columns';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-champions-import-xlsx',
  templateUrl: './champions-import-xlsx.component.html',
  styleUrls: ['./champions-import-xlsx.component.scss']
})
export class ChampionsImportXlsxComponent implements OnInit {
  destroyRef = inject(DestroyRef)
  @Input()
  public schema: any = null;

  @Input()
  public import: FormControl = null;

  @Input()
  public champions: FormArray = null;

  @Input() 
  public teams = []

  @Output()
  public onChange: EventEmitter<any> = new EventEmitter();

  public extensions: string[] = ['xlsx'];

  public errors: string[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private campaignService: CampaignService
  ) { }

  ngOnInit(): void {
    this.import.valueChanges.pipe(
      filter(value => _.isNull(value)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.errors = [];
    });

    this.import.valueChanges.pipe(
      filter(value => !_.isNull(value)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((file: File) => {
      this.onReadFile(file);
    });
  }

  onReadFile(file: File) {
    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      // read workbook and grab the first sheet
      const bstr: string = e.target.result;
      const workbook = new ExcelJS.Workbook();

      workbook.xlsx.load(bstr).then(() => {
        const workSheets = workbook.worksheets;
        let firstSheet = workbook.getWorksheet("Fundraisers") || null;

        if(firstSheet === null && workSheets.length > 1) {
          this.errors = ['Multiple sheets are detected. Please delete extra sheets and try again.'];
          return;
        }

        if(firstSheet === null) {
          firstSheet = workSheets[0] || null;
        }

        if(firstSheet === null) {
          this.errors = ['Error reading the file. Please make sure it is a valid Excel file.'];
          return;
        }
        
        const itemsSheetColumns = [
          { header: 'Name', key: 'name' },
          { header: 'Email', key: 'email' },
          { header: 'Goal', key: 'goal' },
        ]
        if(this.teams.length > 0) {
          itemsSheetColumns.push({ header: 'Team Name', key: 'teamId' })
        }

        let data = [];
        let columns = [];
        let isFirstRow = true;

        firstSheet.eachRow((rawRow) => {
          let row = []
          let value = null; 

          rawRow.eachCell({ includeEmpty: true }, function(cell, colNumber) {
            if(!isFirstRow) {
              return;
            }
            const value = cell.value || '';
            const filteredValue = value.replace(/\s+/g, ' '); // replace all spaces with one space
 
            const itemSheetColumn = itemsSheetColumns.find(itemsSheetColumn => {
              return itemsSheetColumn.header === filteredValue;
            })

            columns.push({...itemSheetColumn, colNumber});
          });

          isFirstRow = false;

          // get the value of each cell
          rawRow.eachCell({ includeEmpty: true }, function(cell) {
            const cellValue = cell.value || '';
            
            switch (cell.type) {
              case ExcelJS.ValueType.Number:
                const numFmt = cell.numFmt || "0.00";
                value = cellValue;

                if(numFmt[numFmt.length - 1] === "%") {
                  value = cellValue * 100;
                }
                break;

              case ExcelJS.ValueType.RichText:
                cellValue.richText.forEach(val => {
                  value += val.text;
                });
                break;

              case ExcelJS.ValueType.Hyperlink:
                value = cellValue.text || '';
                break;
              default:
                value = cellValue;
                break;
            }

            row.push(value);
          });

          let values = _.map(row, (value, index) => {
            const str = new String(value);
            const column = columns.find((column) => {
              return column.colNumber === index + 1;
            }) || null;

            if(column === null) {
              return null;
            }

            return { 
              field: column.key,
              value: str.toString()
            };
          });

          values = _.reject(values, value => value === null);
          
          data.push(values);
        });

        data = _.map(data, row => {
          return _.map(row, cell => {
            return {...cell, value: _.trim(cell.value) };
          });
        });
        data = _.filter(data, row => {
          for(let cell of row) {
            if(cell.value !== '') {
              return true;
            }
          }
          return false;
        });

        this.champions = this.formBuilder.array([], []);
        this.errors = [];

        const result = this.campaignService.transformTo(data, this.schema);
        
        if(result.length < 1) {
          this.errors = ['Could not find Items'];
          return;
        }

        const list = [];
        _.forEach(result, (obj, row) => {
          const option = this.formBuilder.group({});
          this.campaignService.generateForm(option, this.schema);

          const name = option.get('name');
          const email = option.get('email');
          const goal = option.get('goal');
          const teamName = option.get('teamId'); // we enter Team Name, but than replace it with teamId
          name.clearValidators();
          email.clearValidators();
          goal.clearValidators();
          teamName.clearValidators();

          this.campaignService.initializeForm(option, obj, this.schema);

          const isTeamsExist = this.teams.length > 0
          
          if(!isTeamsExist) {
            const teamName = option.get('teamId');
            teamName.clearValidators();
            teamName.updateValueAndValidity();
          }
          
          const updatedTeams = _.map(this.teams, (team) => {
            return { 
              ...team, 
              name: team.name.toLowerCase() 
            };
          });
          
          _.forEach(option.controls, (control, column) => {
            if (control.invalid) {
              _.forEach(control.errors, (value, error)  => {
                if(value) {
                  let errorMessage = `Row ${row + 2}, Column "${FUNDRAISER_COLUMNS[column]}".`;

                  switch(error) {
                    case 'required':
                      errorMessage += ` Value of column "${FUNDRAISER_COLUMNS[column]}" cannot be empty.`;
                      break;
                    case 'min':
                      errorMessage += ` Value of column "${FUNDRAISER_COLUMNS[column]}" cannot be less than ${value.min}.`;
                      break;
                    case 'maxlength':
                      errorMessage += ` Value of column "${FUNDRAISER_COLUMNS[column]}" cannot be longer than ${value.requiredLength}.`;
                      break;
                    default:
                      errorMessage += ' Unknown error.';
                  }
                  this.errors.push(errorMessage);
                } 
              })
            } else if (column === 'email' && !window['utils'].validateEmail(control.value)) {
              const errorMessage = `Row ${row + 2}, Column "${FUNDRAISER_COLUMNS[column]}". Invalid Email`;
              this.errors.push(errorMessage);
            } else if (column === 'teamId' && this.teams.length > 0) {
              const teamName = control.value;
              const errorMessage = `Row ${row + 2}, Column "${FUNDRAISER_COLUMNS[column]}". Team with name "${teamName}" is not found!`;
              const foundTeam = _.find(updatedTeams, {name: teamName.toLowerCase() }) || null;

              if (!isTeamsExist || !foundTeam) {
                this.errors.push(errorMessage);
              } else if (foundTeam) {
                control.setValue(foundTeam.id);
              }
            }
          });
          this.champions.push(option);

          list.push({
            ...this.campaignService.generateRequestObject(option, this.schema),
            id: row
          });
        });

        if(this.errors.length < 1) {
          this.onChange.next(this.champions);
        }
      });
    };
    reader.readAsBinaryString(file);
  }

  onSelect(file: File) {
    this.import.setValue(file);
  }

}
